import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import SEOHead from '../components/SEOHead';

export default function GraphicDesignPage() {
  const router = useRouter();
  
  // English-only page - canonical URL is fixed
  const canonicalUrl = '/graphic-design';

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Graphic Design',
    description: 'Professional graphic design services in Lithuania. Custom design solutions including logos, branding, web design, and marketing materials for businesses seeking visual identity.',
    provider: {
      '@type': 'Organization',
      name: 'LB Visible',
      url: 'https://www.lbvisible.com',
      knowsAbout: [
        'Graphic Design',
        'Brand Identity',
        'Logo Design',
        'Web Design',
        'Marketing Materials',
      ],
    },
    areaServed: {
      '@type': 'Country',
      name: 'Lithuania',
    },
  };

  return (
    <>
      <SEOHead
        title='Graphic Design | LBVisible'
        description='Professional graphic design services in Lithuania. Custom design solutions including logos, branding, web design, and marketing materials for businesses seeking visual identity.'
        keywords='graphic design, logo design, branding, web design, marketing materials, graphic design Lithuania'
        canonicalUrl={canonicalUrl}
        lang='en'
        structuredData={structuredData}
      />

      <main className='min-h-screen max-w-4xl mx-auto px-4 sm:px-6 py-12'>
        <article className='prose prose-lg dark:prose-invert max-w-none'>
          <style jsx global>{`
            article h1 {
              font-size: 2.25rem;
              font-weight: 700;
              margin-top: 2rem;
              margin-bottom: 1.5rem;
              line-height: 1.2;
            }
            article h2 {
              font-size: 1.875rem;
              font-weight: 700;
              margin-top: 2rem;
              margin-bottom: 1rem;
              line-height: 1.3;
            }
            article h3 {
              font-size: 1.5rem;
              font-weight: 600;
              margin-top: 1.5rem;
              margin-bottom: 0.75rem;
              line-height: 1.4;
            }
            article p {
              margin-bottom: 1.5rem;
              line-height: 1.75;
            }
          `}</style>
          <h1 className='text-gray-900 dark:text-white'>
            Graphic Design Services
          </h1>
          
          <h2 className='text-gray-900 dark:text-white'>
            Why Professional Graphic Design Matters
          </h2>
          <p className='text-gray-700 dark:text-gray-400'>
            Graphic design is the visual language of your business. It communicates your brand identity, values, and message to your target audience. Professional graphic design creates memorable visual experiences that differentiate your business from competitors and build trust with customers. Whether you need a logo, branding materials, web design, or marketing collateral, professional graphic design is essential for creating a strong visual presence that resonates with your audience.
          </p>

          <h2 className='text-gray-900 dark:text-white'>
            Our Graphic Design Process
          </h2>
          <p className='text-gray-700 dark:text-gray-400'>
            Our graphic design process begins with understanding your brand, target audience, and design objectives. We research your industry, analyze competitors, and develop design concepts that align with your brand identity and business goals. We create visual solutions that are not only aesthetically pleasing but also functional and effective in communicating your message. Throughout the design process, we maintain clear communication, provide regular updates, and refine designs based on your feedback.
          </p>

          <h3 className='text-gray-900 dark:text-white'>
            Logo Design and Brand Identity
          </h3>
          <p className='text-gray-700 dark:text-gray-400'>
            We create distinctive logos and brand identities that represent your business effectively. Our logo design services include concept development, multiple design options, and final logo files in various formats. We ensure your brand identity is consistent across all touchpoints, creating a cohesive visual experience for your customers.
          </p>

          <h3 className='text-gray-900 dark:text-white'>
            Web Design and User Interface Design
          </h3>
          <p className='text-gray-700 dark:text-gray-400'>
            We design websites and user interfaces that are not only visually appealing but also user-friendly and conversion-focused. Our web design services include layout design, color schemes, typography, and interactive elements that create engaging user experiences. We ensure designs are responsive, accessible, and optimized for various devices and screen sizes.
          </p>

          <h2 className='text-gray-900 dark:text-white'>
            Why Choose LBVisible for Graphic Design
          </h2>
          <p className='text-gray-700 dark:text-gray-400'>
            At LBVisible, we understand that graphic design is more than just creating pretty visuals—it&apos;s about solving business problems through design. We combine creativity with strategic thinking to deliver design solutions that not only look great but also drive results. Our design approach focuses on understanding your business, audience, and objectives to create visuals that effectively communicate your message and support your business goals.
          </p>

          <p className='text-gray-700 dark:text-gray-400'>
            Our graphic design services include logo design, brand identity, web design, marketing materials, and ongoing design support. We work closely with you throughout the design process, ensuring your vision becomes reality and your brand stands out in the marketplace.
          </p>

          <p className='text-gray-700 dark:text-gray-400'>
            Whether you&apos;re launching a new business, rebranding an existing one, or need design support for marketing campaigns, we have the expertise to help you succeed. Contact us today to discuss your graphic design needs and discover how we can help your business create a compelling visual identity.
          </p>
        </article>
      </main>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async () => {
  return {
    props: {},
  };
};

